/**
 * Bug Explorer interactive storytelling components. Each reads its content from the
 * Liquid-rendered DOM (block markup + data attributes) — counts, positions and labels
 * are never hardcoded here.
 *  <be-hotspots> <be-steps> <be-magnifier> <be-scenes> <be-tabs> <be-accordion> <be-carousel>
 */
import { motionFactor, onScrollFrame, clamp } from '@be/core';

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};
const smooth = () => (motionFactor() ? 'smooth' : 'auto');

/* ---------- Hotspots ---------- */
class BeHotspots extends HTMLElement {
  /** @type {AbortController|null} */ #ac = null;
  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener(
      'click',
      (event) => {
        const el = event.target instanceof Element ? event.target.closest('[data-index]') : null;
        if (el) this.select(Number(el.getAttribute('data-index')));
      },
      { signal }
    );
    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener(
        'pointerover',
        (event) => {
          const el = event.target instanceof Element ? event.target.closest('.be-hs__pin, .be-hs--split .be-hs__card') : null;
          if (el) this.select(Number(el.getAttribute('data-index')));
        },
        { signal }
      );
    }
    this.select(0);
  }
  disconnectedCallback() {
    this.#ac?.abort();
  }
  /** @param {number} i */
  select(i) {
    const pins = this.querySelectorAll('.be-hs__pin');
    const pin = pins[i];
    if (!pin) return;
    this.style.setProperty('--rx', `${pin.getAttribute('data-x')}%`);
    this.style.setProperty('--ry', `${pin.getAttribute('data-y')}%`);
    this.querySelectorAll('[data-index]').forEach((el) => {
      el.setAttribute('aria-pressed', Number(el.getAttribute('data-index')) === i ? 'true' : 'false');
    });
    this.querySelectorAll('[data-tip]').forEach((tip) => {
      const active = Number(tip.getAttribute('data-tip')) === i;
      tip.classList.toggle('is-active', active);
    });
  }
}
define('be-hotspots', BeHotspots);

/* ---------- How it works ---------- */
class BeSteps extends HTMLElement {
  /** @type {IntersectionObserver|null} */ #io = null;
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.activate(Number(entry.target.getAttribute('data-step')));
        }
      },
      { rootMargin: '-42% 0px -42% 0px' }
    );
    this.steps.forEach((step) => this.#io?.observe(step));
  }
  disconnectedCallback() {
    this.#io?.disconnect();
  }
  /** @param {number} i */
  activate(i) {
    const total = this.steps.length;
    this.steps.forEach((step, n) => step.classList.toggle('is-active', n === i));
    this.querySelectorAll('[data-layer]').forEach((layer) => layer.classList.toggle('is-active', Number(layer.getAttribute('data-layer')) === i));
    this.querySelectorAll('[data-bar]').forEach((bar) => bar.classList.toggle('is-done', Number(bar.getAttribute('data-bar')) <= i));
    const step = this.steps[i];
    const counter = this.querySelector('[data-counter]');
    const label = this.querySelector('[data-counter-label]');
    if (counter && step) counter.textContent = step.getAttribute('data-number') || String(i + 1).padStart(2, '0');
    if (label && step) label.textContent = `/ ${String(total).padStart(2, '0')} · ${step.getAttribute('data-title') || ''}`;
  }
}
define('be-steps', BeSteps);

/* ---------- Magnifier ---------- */
class BeMagnifier extends HTMLElement {
  /** @type {AbortController|null} */ #ac = null;
  /** @type {ResizeObserver|null} */ #ro = null;
  #target = { x: 0, y: 0 };
  #current = { x: 0, y: 0 };
  #raf = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.lens = /** @type {HTMLElement|null} */ (this.querySelector('[data-lens]'));
    this.inner = /** @type {HTMLElement|null} */ (this.querySelector('[data-lens-inner]'));
    if (!this.lens || !this.inner) return;
    this.zoom = Number(this.dataset.zoom || 3);

    const move = (event) => {
      const r = this.getBoundingClientRect();
      this.#target = { x: clamp(event.clientX - r.left, 0, r.width), y: clamp(event.clientY - r.top, 0, r.height) };
      this.#remember();
      this.#loop();
    };
    this.addEventListener('pointermove', move, { signal, passive: true });
    this.addEventListener('pointerdown', move, { signal, passive: true });
    this.addEventListener(
      'keydown',
      (event) => {
        const step = event.shiftKey ? 60 : 24;
        const map = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
        const delta = map[/** @type {keyof typeof map} */ (event.key)];
        if (!delta) return;
        event.preventDefault();
        this.#target = {
          x: clamp(this.#target.x + delta[0], 0, this.clientWidth),
          y: clamp(this.#target.y + delta[1], 0, this.clientHeight),
        };
        this.#remember();
        this.#loop();
      },
      { signal }
    );
    this.#ro = new ResizeObserver(() => this.#measure());
    this.#ro.observe(this);
    this.#measure();
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ro?.disconnect();
    cancelAnimationFrame(this.#raf);
  }

  /** Lens position as a fraction of the stage, so it survives resizes and hidden states. */
  #ratio = { x: 0.6, y: 0.47 };

  #remember() {
    if (!this.clientWidth || !this.clientHeight) return;
    this.#ratio = { x: this.#target.x / this.clientWidth, y: this.#target.y / this.clientHeight };
  }

  #measure() {
    if (!this.inner || !this.clientWidth || !this.clientHeight) return;
    this.inner.style.width = `${this.clientWidth}px`;
    this.inner.style.height = `${this.clientHeight}px`;
    this.#target = { x: this.clientWidth * this.#ratio.x, y: this.clientHeight * this.#ratio.y };
    this.#current = { ...this.#target };
    this.#place();
  }

  #loop() {
    if (this.#raf) return;
    const step = () => {
      const k = motionFactor() ? 0.22 : 1;
      this.#current.x += (this.#target.x - this.#current.x) * k;
      this.#current.y += (this.#target.y - this.#current.y) * k;
      this.#place();
      if (Math.abs(this.#target.x - this.#current.x) + Math.abs(this.#target.y - this.#current.y) > 0.4) {
        this.#raf = requestAnimationFrame(step);
      } else {
        this.#raf = 0;
      }
    };
    this.#raf = requestAnimationFrame(step);
  }

  #place() {
    if (!this.lens || !this.inner) return;
    const R = this.lens.offsetWidth / 2;
    const { x, y } = this.#current;
    const Z = this.zoom || 3;
    this.lens.style.transform = `translate3d(${x - R}px, ${y - R}px, 0)`;
    this.inner.style.transform = `translate3d(${R - x * Z}px, ${R - y * Z}px, 0) scale(${Z})`;
  }
}
define('be-magnifier', BeMagnifier);

/* ---------- Scenes (pinned horizontal scroll / carousel) ---------- */
class BeScenes extends HTMLElement {
  /** @type {AbortController|null} */ #ac = null;
  /** @type {(() => void)|null} */ #unsub = null;
  /** @type {ResizeObserver|null} */ #ro = null;
  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.track = /** @type {HTMLElement|null} */ (this.querySelector('[data-track]'));
    this.bar = /** @type {HTMLElement|null} */ (this.querySelector('[data-progress]'));
    this.mq = window.matchMedia('(min-width: 990px) and (hover: hover)');
    this.mq.addEventListener('change', () => this.#layout(), { signal });
    this.#ro = new ResizeObserver(() => this.#layout());
    if (this.track) this.#ro.observe(this.track);
    this.#unsub = onScrollFrame(() => this.#frame());
    this.track?.addEventListener('scroll', () => this.#carouselProgress(), { passive: true, signal });
    this.addEventListener(
      'click',
      (event) => {
        const t = event.target instanceof Element ? event.target : null;
        if (!t || !this.track) return;
        const dir = t.closest('[data-prev]') ? -1 : t.closest('[data-next]') ? 1 : 0;
        if (dir) this.track.scrollBy({ left: dir * this.track.clientWidth * 0.8, behavior: smooth() });
      },
      { signal }
    );
    this.#layout();
  }
  disconnectedCallback() {
    this.#ac?.abort();
    this.#unsub?.();
    this.#ro?.disconnect();
  }
  get pinned() {
    return this.dataset.mode === 'pinned' && this.mq?.matches;
  }
  #layout() {
    if (!this.track) return;
    if (this.pinned) {
      const distance = Math.max(0, this.track.scrollWidth - window.innerWidth);
      this.style.height = `${distance + window.innerHeight}px`;
    } else {
      this.style.height = '';
      this.track.style.transform = '';
    }
    this.#frame();
    this.#carouselProgress();
  }
  #frame() {
    if (!this.pinned || !this.track) return;
    const rect = this.getBoundingClientRect();
    const total = this.offsetHeight - window.innerHeight;
    const p = total > 0 ? clamp(-rect.top / total) : 0;
    const distance = Math.max(0, this.track.scrollWidth - window.innerWidth);
    this.track.style.transform = `translate3d(${(-p * distance).toFixed(1)}px, 0, 0)`;
    if (this.bar) this.bar.style.transform = `scaleX(${p})`;
  }
  #carouselProgress() {
    if (this.pinned || !this.track || !this.bar) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const visible = this.track.clientWidth / Math.max(1, this.track.scrollWidth);
    const p = max > 0 ? this.track.scrollLeft / max : 1;
    this.bar.style.transform = `scaleX(${Math.min(1, visible + (1 - visible) * p)})`;
  }
}
define('be-scenes', BeScenes);

/* ---------- Tabs (WAI-ARIA tabs pattern) ---------- */
class BeTabs extends HTMLElement {
  /** @type {AbortController|null} */ #ac = null;
  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.addEventListener(
      'click',
      (event) => {
        const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
        if (tab) this.select(this.tabs.indexOf(tab));
      },
      { signal }
    );
    this.addEventListener(
      'keydown',
      (event) => {
        const current = this.tabs.indexOf(/** @type {Element} */ (event.target));
        if (current < 0) return;
        const n = this.tabs.length;
        const next = { ArrowRight: current + 1, ArrowLeft: current - 1, Home: 0, End: n - 1 }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        const i = ((next % n) + n) % n;
        this.select(i);
        /** @type {HTMLElement} */ (this.tabs[i]).focus();
      },
      { signal }
    );
  }
  disconnectedCallback() {
    this.#ac?.abort();
  }
  /** @param {number} i */
  select(i) {
    this.tabs.forEach((tab, n) => {
      const active = n === i;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
      const panel = document.getElementById(tab.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = !active;
      if (active && tab instanceof HTMLElement) {
        const list = tab.parentElement;
        if (list) list.scrollTo({ left: tab.offsetLeft - list.clientWidth / 2 + tab.clientWidth / 2, behavior: smooth() });
      }
    });
    this.querySelectorAll('[data-layer]').forEach((layer) => layer.classList.toggle('is-active', Number(layer.getAttribute('data-layer')) === i));
  }
}
define('be-tabs', BeTabs);

/* ---------- Accordion ---------- */
class BeAccordion extends HTMLElement {
  /** @type {AbortController|null} */ #ac = null;
  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener(
      'click',
      (event) => {
        const button = event.target instanceof Element ? event.target.closest('.be-acc__btn') : null;
        if (!button || !this.contains(button)) return;
        const open = button.getAttribute('aria-expanded') !== 'true';
        if (open && this.hasAttribute('data-single')) {
          this.querySelectorAll('.be-acc__btn[aria-expanded="true"]').forEach((other) => {
            if (other !== button) this.#set(other, false);
          });
        }
        this.#set(button, open);
      },
      { signal: this.#ac.signal }
    );
  }
  disconnectedCallback() {
    this.#ac?.abort();
  }
  #set(button, open) {
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    const panel = document.getElementById(button.getAttribute('aria-controls') || '');
    panel?.toggleAttribute('data-open', open);
  }
}
define('be-accordion', BeAccordion);

/* ---------- Carousel (snap track + arrows + progress) ---------- */
class BeCarousel extends HTMLElement {
  /** @type {AbortController|null} */ #ac = null;
  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.track = /** @type {HTMLElement|null} */ (this.querySelector('[data-track]'));
    this.bar = /** @type {HTMLElement|null} */ (this.querySelector('[data-progress]'));
    if (!this.track) return;
    const update = () => {
      if (!this.track || !this.bar) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const visible = this.track.clientWidth / Math.max(1, this.track.scrollWidth);
      const p = max > 0 ? this.track.scrollLeft / max : 1;
      this.bar.style.transform = `scaleX(${Math.min(1, visible + (1 - visible) * p)})`;
    };
    this.track.addEventListener('scroll', update, { passive: true, signal });
    window.addEventListener('resize', update, { passive: true, signal });
    this.addEventListener(
      'click',
      (event) => {
        const t = event.target instanceof Element ? event.target : null;
        if (!t || !this.track) return;
        const dir = t.closest('[data-prev]') ? -1 : t.closest('[data-next]') ? 1 : 0;
        if (!dir) return;
        const card = this.track.firstElementChild;
        const step = card instanceof HTMLElement ? card.offsetWidth + 16 : this.track.clientWidth * 0.8;
        this.track.scrollBy({ left: dir * step, behavior: smooth() });
      },
      { signal }
    );
    update();
  }
  disconnectedCallback() {
    this.#ac?.abort();
  }
}
define('be-carousel', BeCarousel);